import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useApolloClients } from '@core/context/ApolloClientsContext';
import { useShopIdsKey, parseShopIdsKey } from '@core/tenant/useShopIdsKey';
import {
  readCatalogCache,
  writeCatalogCache,
} from '@core/cache/queryCache';
import { CATALOG_PAGE_SIZE } from '@core/catalog/catalogConstants';
import { graphqlProductService } from '@features/products/services/graphqlProductService';
import type { Product } from '@features/products/types/index';

type ShopOffsets = Record<string, number>;

export interface CatalogContextValue {
  products: Product[];
  loading: boolean;
  error: Error | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => Promise<void>;
}

const CatalogContext = createContext<CatalogContextValue | null>(null);

export function CatalogProvider({ children }: { children: ReactNode }) {
  const { shops, tenantLoading } = useApolloClients();
  const shopIdsKey = useShopIdsKey();
  const shopIds = useMemo(() => parseShopIdsKey(shopIdsKey), [shopIdsKey]);

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const offsetsRef = useRef<ShopOffsets>({});

  const resetOffsets = useCallback(() => {
    const next: ShopOffsets = {};
    for (const id of shopIds) {
      next[id] = 0;
    }
    offsetsRef.current = next;
  }, [shopIdsKey]);

  const mergePage = useCallback(
    (prev: Product[], page: Product[]) => {
      if (!page.length) return prev;
      const seen = new Set(prev.map(p => p.id));
      const added = page.filter(p => !seen.has(p.id));
      return [...prev, ...added];
    },
    []
  );

  const fetchNextPages = useCallback(
    async (replace: boolean): Promise<void> => {
      if (tenantLoading || !shops.length) {
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const cached = replace ? readCatalogCache<Product[]>(shopIds) : null;
        if (cached?.length) {
          setProducts(cached);
          setHasMore(cached.length >= CATALOG_PAGE_SIZE * shops.length);
          return;
        }

        const pageSlices = await Promise.all(
          shops.map(async shop => {
            const offset = offsetsRef.current[shop.id] ?? 0;
            const slice = await graphqlProductService.fetchProductsPage(
              shop.id,
              offset,
              CATALOG_PAGE_SIZE
            );
            offsetsRef.current[shop.id] = offset + slice.length;
            return slice;
          })
        );
        const page = pageSlices.flat();
        setProducts(prev => (replace ? page : mergePage(prev, page)));
        const anyFull = pageSlices.some(s => s.length >= CATALOG_PAGE_SIZE);
        setHasMore(anyFull);
        if (replace && page.length) {
          writeCatalogCache(shopIds, page);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to load catalog')
        );
      } finally {
        setLoading(false);
      }
    },
    [tenantLoading, shopIdsKey, mergePage, shops]
  );

  const refetch = useCallback(async () => {
    resetOffsets();
    setProducts([]);
    setHasMore(true);
    await fetchNextPages(true);
  }, [fetchNextPages, resetOffsets]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    await fetchNextPages(false);
  }, [hasMore, loading, fetchNextPages]);

  useEffect(() => {
    if (tenantLoading || !shopIds.length) return;
    resetOffsets();
    setProducts([]);
    setHasMore(true);
    void fetchNextPages(true);
    // Intentionally keyed on shopIdsKey only — avoids refetch storm when `shops` array identity changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shopIdsKey, tenantLoading]);

  const value = useMemo(
    (): CatalogContextValue => ({
      products,
      loading,
      error,
      hasMore,
      loadMore,
      refetch,
    }),
    [products, loading, error, hasMore, loadMore, refetch]
  );

  return (
    <CatalogContext.Provider value={value}>{children}</CatalogContext.Provider>
  );
}

export function useCatalog(): CatalogContextValue {
  const ctx = useContext(CatalogContext);
  if (!ctx) {
    throw new Error('useCatalog must be used within CatalogProvider');
  }
  return ctx;
}

/** Safe optional access for components that may render outside provider during tests. */
export function useCatalogOptional(): CatalogContextValue | null {
  return useContext(CatalogContext);
}
